import React, { useCallback, useEffect, useRef, useState } from "react";
import CardList from "../ui/CardList.jsx";
import FullScreenPanel from "../ui/FullScreenPanel.jsx";
import PhotoButton from "../ui/PhotoButton.jsx";
import { phoneUi } from "../ui/phoneUi";
import { shrinkPhoto } from "../ui/shrinkPhoto";
import { deleteReceipt, loadUnlinkedReceipts, receiptIsPdf, receiptUrl, uploadReceipt } from "../../features/receiving/receiving";
import { stamp } from "../../utils/tmDefects";
import "../mobile.css";

// The phone Receiving page (plan Phase 1): photograph the delivery paperwork straight from the
// camera (PIN) or pick a file, and the documents still waiting to be linked to a PO (preview, open,
// delete). Layout only: features/receiving/receiving.js is the desktop page's own code.

export default function ReceivingScreen() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileInput = useRef(null);
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const data = await loadUnlinkedReceipts();
      if (seq !== loadSeq.current) return;
      setDocs(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      if (seq === loadSeq.current) setError(err.message || "Couldn't load the documents.");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => { loadSeq.current += 1; };
  }, [load]);

  async function send(file) {
    // A full-size photo can pass the API's upload limit: at most 2048 px (shrinkPhoto).
    setBusy(true);
    const toSend = /^image\//.test((file && file.type) || "") ? await shrinkPhoto(file) : file;
    setBusy(false);
    const result = await uploadReceipt({ file: toSend, ui: phoneUi, setBusy, onStart: () => setUploadError(null) });
    if (result.cancelled) return;
    setUploadError(result.error || null);
    if (result.reload) load();
  }

  async function remove(doc) {
    const result = await deleteReceipt({ docId: doc.receipt_document_id, ui: phoneUi });
    if (result.reload) {
      setPreview((p) => (p && p.receipt_document_id === doc.receipt_document_id ? null : p));
      load();
    }
  }

  return (
    <div className="jq-screen" data-testid="phone-receiving">
      <p className="jq-muted">
        Photograph the delivery paperwork (bill of lading, packing slip). It waits here until the office links it to
        its purchase order.
      </p>
      <PhotoButton label="Photograph paperwork" onPick={send} busy={busy} />
      <button type="button" className="btn btn-outline-primary btn-lg w-100 mt-2" disabled={busy} onClick={() => fileInput.current && fileInput.current.click()}>
        <i className="fas fa-paperclip me-2" aria-hidden="true" />Choose a file (PDF or photo)
      </button>
      <input
        ref={fileInput}
        type="file"
        accept="image/*,application/pdf"
        className="jq-visually-hidden"
        aria-label="Choose a receiving document"
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          e.target.value = "";
          if (f) send(f);
        }}
      />
      {uploadError && <div className="jq-note jq-note-danger mt-2" role="alert">{uploadError}</div>}

      <h2 className="jq-section-title mt-3">Waiting to be linked<span className="jq-queue-count">{docs.length}</span></h2>
      <CardList
        items={docs}
        getKey={(d) => d.receipt_document_id}
        onItemClick={setPreview}
        renderCard={(d) => (
          <div className="d-flex align-items-center gap-2">
            <i className={`fas ${receiptIsPdf(d) ? "fa-file-pdf" : "fa-file-image"} fa-lg`} style={{ color: "var(--jq-ph-accent)" }} aria-hidden="true" />
            <div style={{ minWidth: 0 }}>
              <div className="jq-file-name">{d.original_filename}</div>
              <div className="jq-muted">
                #{d.receipt_document_id}
                {d.uploaded_by ? ` · ${d.uploaded_by}` : ""}
                {d.uploaded_at ? ` · ${stamp(d.uploaded_at)}` : ""}
              </div>
            </div>
          </div>
        )}
        loading={loading}
        error={error}
        empty="Nothing waiting: every document is linked."
        testId="phone-receiving-list"
      />

      {preview && (
        <FullScreenPanel
          open
          title={`Document #${preview.receipt_document_id}`}
          onClose={() => setPreview(null)}
          testId="receiving-preview"
          footer={
            <div className="jq-two-buttons">
              <a className="btn btn-outline-primary btn-lg" href={receiptUrl(preview)} target="_blank" rel="noreferrer">Open</a>
              <button type="button" className="btn btn-outline-danger btn-lg" onClick={() => remove(preview)}>Delete</button>
            </div>
          }
        >
          <div className="fw-semibold mb-2">{preview.original_filename}</div>
          {receiptIsPdf(preview) ? (
            <div className="jq-note jq-note-info">A PDF: tap Open to read it.</div>
          ) : (
            <img src={receiptUrl(preview)} alt={preview.original_filename} className="w-100" style={{ borderRadius: 10 }} />
          )}
          <div className="jq-muted mt-2">
            {preview.uploaded_by ? `By ${preview.uploaded_by}` : "Uploaded"}
            {preview.uploaded_at ? ` · ${stamp(preview.uploaded_at)}` : ""}
          </div>
        </FullScreenPanel>
      )}
    </div>
  );
}
